// ================================================================================================
import _ from "lodash";
import { ReactNode, Component, PureComponent, CSSProperties, MouseEvent, ReactElement } from 'react';
import moment from "moment";
import { DateFormat, Formatters } from "@Utils/Formats";
import { isDefined, IsNullOrEmpty, IsNullOrWhiteSpace, getBool } from "@Utils/Micro";
import { ItemsStyle, GetReactStyle, getZDepthShadow } from "@Utils/Colors";
import { ErrorCatch } from "./ErrorCatch";
import { Button, ButtonValueType } from './Button';
import { popupStaticWindow } from '@Utils/Utils';
import { Markdown } from './Markdown/MarkdownLazy';
import { sanitizeUrl } from '@Utils/SanitizeURL';
import { isFunction } from '@Utils/Micro';
import Toggle from '@Primitive/Toggle';
import Checkbox from '@Primitive/Checkbox';
import ListItem from '@Primitive/ListItem';
import { Progress, Tooltip, Whisper } from 'rsuite';
import TextBox from '@Primitive/TextBox';
import { Props } from "@Utils/ReactProps";

// ================================================================================================
interface IUserInput {
    isUserInput?: boolean;
}
// ------------------------------------------------------------------------------------------------
export enum InfoTipLayout {
    Default,
    BigIcon
}
// ------------------------------------------------------------------------------------------------
export interface FontSettings {
    size?: number;
    color?: string;
    weight?: "bold" | "normal";
}
// ------------------------------------------------------------------------------------------------
export interface AdvancedInfoTip {
    key?: string;
    imageUrl?: string;

    icon?: string;
    iconSize?: number;

    title?: string;
    titleUrlLink?: string;
    titleFontSettings?: FontSettings;

    subtitle?: string;
    subtitleUrlLink?: string;
    subtitleFontSettings?: FontSettings;

    text?: string;
    textFontSettings?: FontSettings;

    items?: InfoTipItem[];
    backgroundColor?: string;
    fontColor?: string;

    groupOpenIcon?: string;
    groupCloseIcon?: string;
    groupAlignLeft?: boolean;
}
// ------------------------------------------------------------------------------------------------
export interface InfoTipItem {
    type: InfoTipItemType;
    label: string;
    value: any | string[][];
    settings?: any;

    icon?: string;
    style?: ItemsStyle;
    valueStyle?: ItemsStyle;
    items?: InfoTipItem[];

    groupOpenIcon?: string;
    groupCloseIcon?: string;
    groupAlignLeft?: boolean;
}
// ------------------------------------------------------------------------------------------------
export enum InfoTipItemType {
    checkbox = 'checkbox',
    percentage = 'percentage',
    button = 'button',
    datetime = 'datetime',
    table = 'table',
    switch = 'switch',
    text = 'text',
    image = 'image',
    group = 'group',
    markdown = 'markdown'
}
// ------------------------------------------------------------------------------------------------
export interface MapTableSettings {
    /**
     * Split the spaces of columns
     * Default: true
     */
    uniform?: boolean;

    /**
     * style specific columns (dictionary from index to style)
     */
    columnsStyle?: { [key: number]: ItemsStyle };

    /**
     * Tooltip per cell
     */
    tooltips?: string[][];

    /** 
     * Positioning of the tooltips 
     * @default: topStart
     */
    tooltipsPosition?: 'top'
    | 'bottom'
    | 'right'
    | 'left'
    | 'bottomStart'
    | 'bottomEnd'
    | 'topStart'
    | 'topEnd'
    | 'leftStart'
    | 'leftEnd'
    | 'rightStart'
    | 'rightEnd'
    | 'auto'
    | 'autoVerticalStart'
    | 'autoVerticalEnd'
    | 'autoHorizontalStart'
    | 'autoHorizontalEnd';
}

export interface MapDatetimeSettings {
    format?: string;
    seperateLine?: boolean;
}
export interface MapCheckboxSettings extends IUserInput { }
export interface MapSwitchSettings extends IUserInput { }
export interface MapTextSettings extends IUserInput { }
export interface MapPercentageSettings { }
export interface MapButtonSettings {
    pageUrl?: string;
    borderRadius?: number;
    backgroundColor?: string;
    fontColor?: string;
    icon?: string;
    iconType?: ButtonValueType
    iconFontSize?: number;
    uppercase?: boolean;
    shadowLevel?: number;
    tooltip?: string;
}
export interface InfoTipItemGroupSettings {
    // default: false
    startOpen?: boolean;
}
// ------------------------------------------------------------------------------------------------
export interface InfoTipsTheme {
    Layout: InfoTipLayout;
    BigIconSize?: number;
    FontColor: string;
    FontSize: number;
    BackgroundColor: string;
    BorderRadius: number; // In Pixel
    cardStyle?: CSSProperties;
    shadowLevel?: number;
    groupOpenIcon?: string;
    groupCloseIcon?: string;
    groupAlignLeft?: boolean;
}
// ------------------------------------------------------------------------------------------------
const stopEvent = (e: MouseEvent<{}>) => {
    if (_.isEmpty(e))
        return;
    // Stopping bubbling and prevent action.
    e.stopPropagation();
    e.preventDefault();
};

const updatePopup = (i: InfoTipItem, v: any) => {
    if (_.isEmpty(i))
        return;
    // Update item's value.
    i.value = v;
};

function createItem(
    i: InfoTipItem,
    idx: number,
    infoTip: AdvancedInfoTip,
    infoTheme: InfoTipsTheme,
    handlePopupDataChange: (i: InfoTipItem, rerender: boolean, infoTip: AdvancedInfoTip, iconClicked: boolean) => void
): ReactNode {
    const fontColor = i.style?.fontColor ?? infoTip?.fontColor ?? infoTheme.FontColor;

    switch (i.type) {
        // Item is a checkbox element.
        case InfoTipItemType.checkbox: {

            // Get item settings.
            const settings: MapCheckboxSettings = i.settings;
            const isUserInput = !_.isEmpty(settings) && settings.isUserInput == true;
            const checkboxValue = getBool(i.value);
            return <ListItem
                noHover
                style={{ height: 25 }}
                primaryText={i.label}
                primaryTextStyle={{
                    padding: '4px 0px',
                    fontSize: infoTheme.FontSize,
                    color: fontColor
                }}
                rightIcon={<Checkbox
                    key={'checkbox-' + idx}
                    value={checkboxValue}
                    onClick={!isUserInput ? null : (e: MouseEvent<{}>) => {
                        // Stop event or update and send popup's data.
                        if (!isUserInput)
                            stopEvent(e);
                        else {
                            updatePopup(i, !checkboxValue);
                            handlePopupDataChange(i, true, infoTip, false);
                        }
                    }} />} />;
        }

        // Item is a percentage element.
        case InfoTipItemType.percentage:
            let color = i.style?.percentageColor ?? undefined;

            return <div
                key={'percentage-' + idx}
                style={{
                    padding: (idx == infoTip?.items?.length - 1) ? 4 : '2px 4px',
                    fontSize: infoTheme.FontSize
                }}>
                <div style={{ paddingBottom: 2 }}>
                    {!_.isEmpty(i.label) ? i.label + ' - ' + i.value + '%' : i.value + '%'}
                </div>
                <Progress.Line
                    key={'progress-' + idx}
                    style={{ padding: 0 }}
                    showInfo={false}
                    percent={i.value}
                    strokeWidth={6}
                    strokeColor={isDefined(color) ? color : i.value <= 60 ? 'green'
                        : (i.value <= 80 ? 'orange' : 'red')} />
            </div>

        // Item is a button element.
        case InfoTipItemType.button: {
            let settings = i.settings as MapButtonSettings;
            if (settings == null)
                settings = {};

            if (settings.backgroundColor == null)
                settings.backgroundColor = "rgb(0, 188, 212)";

            if (settings.fontColor == null)
                settings.fontColor = "white";

            return <Button
                key={'button-' + idx}
                label={i.label}
                labelAutoSize={false}

                borderRadius={settings.borderRadius}
                shadowLevel={settings.shadowLevel}
                tooltip={settings.tooltip}

                icon={settings.icon}
                iconFontSize={settings.iconFontSize}
                iconType={settings.iconType}
                iconFontColor={settings.fontColor}

                style={{
                    backgroundColor: settings.backgroundColor,
                    color: settings.fontColor,
                    textTransform: settings.uppercase ? 'uppercase' : 'initial',
                    margin: 4,
                    paddingTop: 2,
                    paddingBottom: 2,
                    fontSize: infoTheme.FontSize,
                    height: i.style?.height ?? 20
                }}

                onClick={() => {
                    handlePopupDataChange(i, false, infoTip, false);

                    if (isDefined(settings) &&
                        !IsNullOrEmpty(settings.pageUrl))
                        popupStaticWindow(settings.pageUrl, true);
                }} />;
        }

        // Item is a Table element.
        case InfoTipItemType.table: {
            let settings = i.settings as MapTableSettings;
            let colWidth = undefined;

            if (!isDefined(settings) || !_.has(settings, "uniform") || settings.uniform) {
                colWidth = ((_.isArray(i.value) && i.value.length > 0) ? Math.floor(100 / i.value[0].length) : 0) + "%";
            }

            return <div
                key={'table-' + idx}>
                <table
                    key={'table-' + idx}
                    style={{
                        width: "100%",
                        padding: "2px 0"
                    }}>
                    <tbody>
                        {_.map(i.value, (v: string[], _i: number) => {
                            return <tr key={'tr-' + _i}>
                                {_.map(v, (_v: string, j: number) => {
                                    let tdStyle;
                                    if (_.has(settings?.columnsStyle, j)) {
                                        tdStyle = GetReactStyle(settings.columnsStyle[j]);
                                    }
                                    let tooltip = settings?.tooltips?.[_i]?.[j] ?? undefined;
                                    if (!IsNullOrWhiteSpace(tooltip))
                                        return <Whisper
                                            placement={settings?.tooltipsPosition ?? "topStart"}
                                            trigger="hover"
                                            speaker={<Tooltip>{tooltip}</Tooltip>}
                                        >
                                            <td
                                                key={'tr-' + j}
                                                style={{
                                                    padding: '0 4px',
                                                    width: colWidth,
                                                    wordBreak: 'break-word',
                                                    ...tdStyle
                                                }}>
                                                {_v}
                                            </td>
                                        </Whisper>

                                    return (
                                        <td
                                            key={'tr-' + j}
                                            style={{
                                                padding: '0 4px',
                                                width: colWidth,
                                                wordBreak: 'break-word',
                                                ...tdStyle
                                            }}>
                                            {_v}
                                        </td>
                                    );
                                })}
                            </tr>
                        })}
                    </tbody>
                </table>
            </div>
        }

        // Item is a datetime element.
        case InfoTipItemType.datetime: {

            // Get item's settings.
            const settings = i.settings as MapDatetimeSettings;
            let format = DateFormat.Short;

            let seperateLine = true;
            if (isDefined(settings)) {
                seperateLine = isDefined(settings?.seperateLine) ? settings?.seperateLine : true;
            }

            if (!_.isEmpty(settings) &&
                !_.isEmpty(settings.format)) {

                // Set datetime's format to display.
                switch (settings.format.toLowerCase()) {
                    case 'short':
                        format = DateFormat.Short;
                        break;
                    case 'long':
                        format = DateFormat.Long;
                        break;
                    case 'iso':
                        format = DateFormat.ISO;
                        break;
                    case 'iso ms':
                        format = DateFormat.ISOMS;
                        break;
                    default:
                        break;
                }
            }
            let datetime = null;
            try {
                datetime = moment.utc(i.value).toDate();
            } catch (ex) { }

            return <div
                key={'datetime-' + idx}
                style={{
                    padding: '2px 4px',
                    display: 'flex',
                    flexDirection: seperateLine ? 'column' : 'row'
                }}>
                <div style={{ paddingBottom: 2, width: seperateLine ? null : "50%" }}>
                    {i.label}
                </div>
                <div style={{
                    padding: seperateLine ? 0 : '0 4px',
                    ...GetReactStyle(i.valueStyle)
                }}>
                    {Formatters.datetime(datetime, format)}
                </div>
            </div>
        }

        // Item is a switch element.
        case InfoTipItemType.switch: {

            // Get item's settings.
            const settings = i.settings as MapSwitchSettings;
            const isUserInput = !_.isEmpty(settings) && settings.isUserInput == true;
            const toggleValue = getBool(i.value);

            return <Toggle
                key={'toggle-' + idx}
                style={{
                    fontSize: infoTheme.FontSize,
                    fontWeight: 'normal',
                    color: fontColor
                }}
                disabled={!isUserInput}
                label={i.label}
                toggled={toggleValue}
                onToggle={(checked, e) => {
                    // Stop event or update and send popup's data.
                    if (!isUserInput)
                        stopEvent(e as any);
                    else {
                        updatePopup(i, checked);
                        handlePopupDataChange(i, true, infoTip, false);
                    }
                }} />;
        }

        case InfoTipItemType.text: {
            // Get item's settings.
            const settings = i.settings as MapTextSettings;
            const isUserInput = !_.isEmpty(settings) &&
                settings.isUserInput == true;

            if (!isUserInput)
                return <div
                    key={'text-' + idx}
                    style={{
                        padding: '0px 4px',
                        whiteSpace: "pre-wrap"
                    }}>
                    <div style={{ paddingBottom: 2 }}>{i.label}</div>
                    <div>{i.value}</div>
                </div>

            let boxColor = i.style?.backgroundColor;
            if (IsNullOrEmpty(boxColor)) {
                boxColor = infoTip?.backgroundColor;
                if (IsNullOrEmpty(boxColor))
                    boxColor = infoTheme.BackgroundColor;
            }

            // Add textarea with send button.
            return <div key={'text-field-wrapper-' + idx} style={{ position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span>{i.label}</span>
                    <i className='fa fa-paper-plane'
                        style={{
                            cursor: 'pointer',
                            paddingLeft: 4,
                            color: fontColor
                        }}
                        onClick={() => handlePopupDataChange(i, false, infoTip, false)} />
                </div>
                <TextBox
                    fullWidth
                    type='textarea'
                    key={'text-field-' + idx}
                    style={{
                        color: fontColor,
                        fontSize: infoTheme.FontSize,
                        backgroundColor: boxColor
                    }}
                    disabled={!isUserInput}
                    value={i.value}
                    onChangeString={newValue => {
                        updatePopup(i, newValue);
                        handlePopupDataChange(i, true, infoTip, false);
                    }} />
            </div>;
        }

        case InfoTipItemType.image: {
            return <img
                style={{
                    objectFit: "contain",
                    width: "100%",
                    ...GetReactStyle(i.style)
                }}
                src={i.value} />;
        }

        case InfoTipItemType.markdown: {
            return <Markdown style={{ padding: 5 }} source={i.value} />;
        }

        default:
            return null;
    }
}

// ------------------------------------------------------------------------------------------------

export function getIcon(size: number,
    infoTip: AdvancedInfoTip,
    style: CSSProperties,
    handlePopupDataChange: (i: InfoTipItem, rerender: boolean, infoTip: AdvancedInfoTip, iconClicked: boolean) => void
): ReactElement {
    let iconSize = infoTip.iconSize ?? size;
    if (!_.isEmpty(infoTip.imageUrl))
        return <img
            style={{
                width: iconSize,
                height: iconSize,
                objectFit: 'contain',
                ...style
            }}
            src={infoTip.imageUrl}
            onClick={isDefined(handlePopupDataChange) ? () => handlePopupDataChange(null, false, infoTip, true) : undefined}
        />;
    else
        return <i
            style={{
                width: iconSize,
                height: iconSize,
                fontSize: iconSize,
                textAlign: "center",
                ...style
            }}
            className={infoTip.icon}
            onClick={isDefined(handlePopupDataChange) ? () => handlePopupDataChange(null, false, infoTip, true) : undefined} />;
}
// ------------------------------------------------------------------------------------------------
function renderText(text: string, settings: FontSettings, exStyle: CSSProperties = null, textUrlLink: string = null) {
    if (IsNullOrWhiteSpace(text))
        return null;

    let style = {
        display: "block",
        color: settings.color,
        fontWeight: settings.weight,
        fontSize: settings.size,
        width: 'initial',
        ...exStyle
    };

    if (!IsNullOrWhiteSpace(textUrlLink))
        return <a style={style} href={sanitizeUrl(textUrlLink)} rel="noopener noreferrer" target="_blank">
            {text}
        </a>;

    return <span style={style}>{text}</span>;
}
// ------------------------------------------------------------------------------------------------
export interface InfoTipItemComponentProps {
    infoTipItem: InfoTipItem;
    idx: number;
    infoTip: AdvancedInfoTip;
    infoTheme: InfoTipsTheme;
    handlePopupDataChange: (i: InfoTipItem, rerender: boolean, infoTip: AdvancedInfoTip, iconClicked: boolean) => void;
    fontColor: string;
}
// ------------------------------------------------------------------------------------------------
export interface InfoTipItemComponentStates {
    isGroupClosed: boolean;
}
// ------------------------------------------------------------------------------------------------
export class InfoTipItemComponent extends Component<InfoTipItemComponentProps, InfoTipItemComponentStates> {
    constructor(props) {
        super(props);
        let startOpen = false;
        if (props.infoTipItem?.type == "group") {
            startOpen = props.infoTipItem?.settings?.startOpen ?? false;
        }

        this.state = {
            isGroupClosed: !startOpen
        };
    }
    // --------------------------------------------------------------------------------------------
    render() {
        let {
            infoTipItem,
            idx,
            infoTip,
            infoTheme,
            handlePopupDataChange,
            fontColor
        } = this.props;

        if (!isDefined(infoTipItem))
            return null;

        let fontSize = infoTheme.FontSize;
        let style: CSSProperties = GetReactStyle(infoTipItem.style);
        let localFontColor = infoTipItem?.style?.fontColor ?? fontColor;
        let result = null;

        let bodyItem = null;
        if (infoTipItem.type == InfoTipItemType.group) {
            let groupOpenIcon = infoTipItem?.groupOpenIcon ?? infoTip?.groupOpenIcon ?? infoTheme.groupOpenIcon ?? 'fas fa-angle-down';
            let groupCloseIcon = infoTipItem?.groupCloseIcon ?? infoTip?.groupCloseIcon ?? infoTheme.groupCloseIcon ?? 'fas fa-angle-left';
            let groupIconAlignLeft = infoTipItem?.groupAlignLeft ?? infoTip?.groupAlignLeft ?? infoTheme.groupAlignLeft;

            bodyItem = <div
                className="settingslist-submenu"
                style={{
                    width: "auto",
                    cursor: "pointer",
                    paddingLeft: 0,
                    justifyContent: (groupIconAlignLeft) ? 'unset' : 'space-between'
                }}
                onClick={ev => {
                    ev.stopPropagation();
                    this.setState({ isGroupClosed: !this.state.isGroupClosed });
                }}
                onDoubleClick={ev => ev.stopPropagation()}>
                {(groupIconAlignLeft) ?
                    <span className="settingslist-submenu-icon-left" >
                        <i className={"fas " + (!this.state.isGroupClosed ? groupOpenIcon : groupCloseIcon)} />
                    </span> : null}
                <div
                    key={'text-' + idx}
                    style={{
                        padding: '2px 4px'
                    }}>

                    <div style={{ paddingBottom: 2, justifyContent: 'flex-start' }}>
                        {infoTipItem.label}
                    </div>
                    <div>
                        {infoTipItem.value}
                    </div>
                </div>
                {(groupIconAlignLeft) ? null :
                    <span className="settingslist-submenu-icon-left" >
                        <i className={"fas " + (!this.state.isGroupClosed ? groupOpenIcon : groupCloseIcon)} />
                    </span>}
            </div>
        } else {
            bodyItem = createItem(infoTipItem, idx, infoTip, infoTheme, handlePopupDataChange);
        }

        // Construct the container
        if (!_.isEmpty(infoTipItem.icon)) {
            result = <div
                key={"flex-div-" + idx}
                style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    margin: (infoTipItem.type == InfoTipItemType.group) ? "2px 0" : undefined,
                    ...style
                }}>
                <i style={{
                    width: fontSize,
                    height: fontSize,
                    fontSize: fontSize,
                    marginLeft: 5,
                    marginRight: 10,
                    color: localFontColor
                }} className={infoTipItem.icon} />
                <div style={{ flex: "auto" }}>
                    {bodyItem}
                </div>
            </div>
        } else {
            result = <div key={"div-" + idx} style={{
                margin: (infoTipItem.type == InfoTipItemType.group) ? "2px 0" : undefined,
                ...style
            }}> {bodyItem}</div>
        }

        if (infoTipItem.type != InfoTipItemType.group ||
            this.state.isGroupClosed)
            return result;
        return _.flatMap([
            result,
            _.map(infoTipItem.items, (x, idx) => <InfoTipItemComponent
                key={"item-" + idx}
                infoTipItem={x}
                idx={idx}
                infoTip={infoTip}
                infoTheme={infoTheme}
                handlePopupDataChange={handlePopupDataChange}
                fontColor={fontColor}
            />)
        ]);
    }
    // --------------------------------------------------------------------------------------------
}
// ------------------------------------------------------------------------------------------------
export interface InfoTipCoreProps extends Props<InfoTipCore> {
    width: number;
    infoTip: AdvancedInfoTip;
    infoTheme: InfoTipsTheme;
    handlePopupDataChange: (i: InfoTipItem, rerender: boolean, infoTip: AdvancedInfoTip, iconClicked: boolean) => void;
    handleInfoTipClick?: (infoTip: AdvancedInfoTip, type: 'doubleClick' | 'singleClick') => void
}

export class InfoTipCore extends PureComponent<InfoTipCoreProps> {
    render() {
        let {
            width,
            infoTip,
            infoTheme,
            handlePopupDataChange
        } = this.props;

        let key = infoTip.key;

        // Cannot pass empty container or info tip.
        if (_.isEmpty(key) || _.isEmpty(infoTip))
            return null;

        const hasImage: boolean = !_.isEmpty(infoTip.imageUrl) || !_.isEmpty(infoTip.icon);
        const hasTitle: boolean = !IsNullOrWhiteSpace(infoTip.title) || !IsNullOrWhiteSpace(infoTip.subtitle);
        const backgroundColor = _.isEmpty(infoTip.backgroundColor) ? infoTheme.BackgroundColor : infoTip.backgroundColor;
        const fontColor = _.isEmpty(infoTip.fontColor) ? infoTheme.FontColor : infoTip.fontColor;

        const titleFontSettings = (isDefined(infoTip.titleFontSettings))
            ? infoTip.titleFontSettings
            : {
                color: "inherit",
                size: infoTheme.FontSize + 2
            };

        const subtitleFontSettings = (isDefined(infoTip.subtitleFontSettings))
            ? infoTip.subtitleFontSettings
            : {
                color: "inherit",
                size: infoTheme.FontSize + 1
            };

        const textFontSettings = (isDefined(infoTip.textFontSettings))
            ? infoTip.textFontSettings
            : {
                color: "inherit",
                size: infoTheme.FontSize + 1
            };

        const boxShadow = infoTheme.shadowLevel != null ? getZDepthShadow(infoTheme.shadowLevel) : getZDepthShadow(1);
        switch (infoTheme.Layout) {
            case InfoTipLayout.Default:
                return <div
                    key={'card-' + key}
                    style={{
                        width,
                        zIndex: 1,
                        fontSize: infoTheme.FontSize,
                        borderTopLeftRadius: infoTheme.BorderRadius,
                        borderTopRightRadius: infoTheme.BorderRadius,
                        borderBottomRightRadius: infoTheme.BorderRadius,
                        borderBottomLeftRadius: infoTheme.BorderRadius,
                        backgroundColor: backgroundColor,
                        color: fontColor,
                        padding: 4,
                        boxShadow: boxShadow,
                        ...infoTheme.cardStyle
                    }}
                    onDoubleClick={isFunction(this.props.handleInfoTipClick) ? () => this.props.handleInfoTipClick(infoTip, "doubleClick") : undefined}
                    onClick={isFunction(this.props.handleInfoTipClick) ? () => this.props.handleInfoTipClick(infoTip, "singleClick") : undefined}>
                    <ErrorCatch>
                        {hasTitle || hasImage
                            ? <div
                                style={{
                                    padding: "2px 4px",
                                    fontWeight: 500,
                                    boxSizing: "border-box",
                                    position: "relative",
                                    whiteSpace: "nowrap"
                                }}
                                onClick={() => handlePopupDataChange(null, false, infoTip, true)}>
                                {hasImage
                                    ? getIcon(40, infoTip,
                                        {
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            marginRight: 14,
                                            color: fontColor
                                        }, null)
                                    : null}
                                {hasTitle
                                    ? <div style={{
                                        display: "inline-block",
                                        verticalAlign: "top",
                                        whiteSpace: "normal",
                                        paddingRight: 8,
                                        width: hasImage ? 'calc(100% - 40px)' : 'initial'
                                    }}>
                                        {renderText(infoTip.title, titleFontSettings, null, infoTip.titleUrlLink)}
                                        {renderText(infoTip.subtitle, subtitleFontSettings, { opacity: 0.6 }, infoTip.subtitleUrlLink)}
                                    </div>
                                    : null}
                            </div> : null}
                        {renderText(infoTip.text, textFontSettings, { padding: "2px 4px" })}
                        {_.map(infoTip.items, (i: InfoTipItem, idx: number) => <InfoTipItemComponent
                            key={"item-" + idx}
                            infoTipItem={i}
                            idx={idx}
                            infoTip={infoTip}
                            infoTheme={infoTheme}
                            handlePopupDataChange={(i: InfoTipItem, rerender: boolean, infoTip: AdvancedInfoTip, iconClicked: boolean) => {
                                if (isFunction(handlePopupDataChange))
                                    handlePopupDataChange(i, rerender, infoTip, iconClicked);
                                if (rerender)
                                    this.forceUpdate();
                            }}
                            fontColor={fontColor}
                        />)}
                    </ErrorCatch>
                </div>
            case InfoTipLayout.BigIcon:
                return <div
                    key={'card-' + key}
                    style={{
                        width,
                        fontSize: infoTheme.FontSize,
                        borderTopLeftRadius: infoTheme.BorderRadius,
                        borderTopRightRadius: infoTheme.BorderRadius,
                        borderBottomRightRadius: infoTheme.BorderRadius,
                        borderBottomLeftRadius: infoTheme.BorderRadius,
                        backgroundColor: backgroundColor,
                        color: fontColor,
                        padding: 4,
                        ...infoTheme.cardStyle
                    }}>
                    <ErrorCatch>
                        <div
                            key={"card-text-" + key}>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    color: fontColor
                                }}>
                                {getIcon((isDefined(infoTheme.BigIconSize)) ? infoTheme.BigIconSize * width : 0.75 * width,
                                    infoTip,
                                    { color: fontColor },
                                    handlePopupDataChange)}
                            </div>
                            {renderText(infoTip.title, titleFontSettings, { textAlign: "center" }, infoTip.titleUrlLink)}
                            {renderText(infoTip.subtitle, subtitleFontSettings, { textAlign: "center", opacity: 0.6 }, infoTip.subtitleUrlLink)}
                            {renderText(infoTip.text, textFontSettings, { padding: "2px 4px" })}
                        </div>
                        {_.map(infoTip.items, (i: InfoTipItem, idx: number) => <InfoTipItemComponent
                            key={"item-" + idx}
                            infoTipItem={i}
                            idx={idx}
                            infoTip={infoTip}
                            infoTheme={infoTheme}
                            handlePopupDataChange={(i: InfoTipItem, rerender: boolean, infoTip: AdvancedInfoTip, iconClicked: boolean) => {
                                if (isFunction(handlePopupDataChange))
                                    handlePopupDataChange(i, rerender, infoTip, iconClicked);
                                if (rerender)
                                    this.forceUpdate();
                            }}
                            fontColor={fontColor}
                        />)}
                    </ErrorCatch>
                </div>;
        }
    }
}
// ------------------------------------------------------------------------------------------------
export function renderInfoTip(
    key: string,
    width: number,
    infoTip: AdvancedInfoTip,
    infoTheme: InfoTipsTheme,
    handlePopupDataChange: (i: InfoTipItem, rerender: boolean, infoTip: AdvancedInfoTip, iconClicked: boolean) => void,
    handleInfoTipClick?: (infoTip: AdvancedInfoTip) => void
): ReactNode {
    // Cannot pass empty container or info tip.
    if (_.isEmpty(key) || _.isEmpty(infoTip))
        return null;
    if (_.isEmpty(infoTip.key))
        infoTip.key = key;
    return <InfoTipCore
        key={key}
        width={width}
        infoTip={infoTip}
        infoTheme={infoTheme}
        handlePopupDataChange={handlePopupDataChange}
        handleInfoTipClick={handleInfoTipClick}
    />;
}
// ------------------------------------------------------------------------------------------------
export function createPortStateFromInfoTipEvent(i: InfoTipItem, infoTip: AdvancedInfoTip, iconClicked: boolean): any {
    if (iconClicked)
        return {
            infoTip: infoTip,
            item: { type: "icon" }
        };
    else
        return {
            infoTip: infoTip,
            item: i,
            value: (isDefined(i)) ? i.value : null
        };
}
// ------------------------------------------------------------------------------------------------
// BACKWARDS COMPATIBILITY WARNING
// FOR NOW, DON'T UPDATE SCHEMA VALIDATOR AT OLD WIDGETS.
// USE IT ONLY AT NEW WIDGETS!
export const AdvancedInfoTipAjvSchema = {
    type: 'object',
    properties: {
        imageUrl: { type: 'string' },
        icon: { type: 'string' },
        title: { type: 'string' },
        subtitle: { type: 'string' },
        text: { type: 'string' },
        items: {
            type: 'array',
            items: {
                type: 'object',
                properties: {
                    type: {
                        type: 'string',
                        enum: ['checkbox', 'percentage', 'button', 'datetime', 'table', 'switch', 'text', 'image', 'group', 'markdown']
                    },
                    label: { type: 'string' },
                    value: {
                        type: ['string', 'number', 'boolean', 'object', 'array']
                    },
                    settings: {
                        type: 'object',
                        properties: {
                            format: { type: 'string' },
                            pageUrl: { type: 'string' },
                            isUserInput: { type: 'boolean' },
                        }
                    },
                    icon: { type: 'string' },
                },
                required: ['type', 'label']
            }
        },
        backgroundColor: {
            type: 'string',
        },
        fontColor: {
            type: 'string',
        }
    }
}
// ------------------------------------------------------------------------------------------------